import { type ReactElement, useMemo } from "react";

import type { BaseClickableWithRCAndDomProps } from "@/types";

import { useConvertSxToEmotionStyles } from "@/hooks/useConvertSxToEmotionStyles";
import { cloneElementWithCssProp, mergeSx } from "@/utils";

const DEFAULT_RC = <button type="button" />;

export function BaseClickable<RC extends ReactElement | undefined = undefined>({
  rc = DEFAULT_RC,
  testId,
  sx,
  children,
  domRef,
  className,
  ...props
}: BaseClickableWithRCAndDomProps<RC>) {
  const { onClick, ...otherProps } =
    "onClick" in props ? props : { ...props, onClick: null };

  const mergedSx = useMemo(
    () =>
      mergeSx(
        {
          // @NOTE Some basic reset styles:
          padding: "0",
          border: "none",
          background: "transparent",
          display: "inline-flex",
          alignItems: "baseline",
          cursor: "pointer",
          textDecoration: "none",
        },
        sx,
      ),
    [sx],
  );

  const convertedCss = useConvertSxToEmotionStyles(mergedSx);
  const mergedClassNames = `${className ?? ""} ${rc.props.className ?? ""} BaseClickable`;

  return cloneElementWithCssProp(rc, {
    ...otherProps,
    ...(testId ? { "data-testid": testId } : {}),
    ...(domRef ? { ref: domRef } : {}),
    ...(onClick ? { onClick } : {}),
    className: mergedClassNames,
    css: convertedCss,
    children,
  });
}

BaseClickable.displayName = "BaseClickable";
